import {
  SECTION_NAME_PROMPT_TITLE,
  sectionBaseText,
} from '@src/services/reel/reelNamingBaseText';
import { LogManager } from '@src/utility/Logger';
import { useCallback } from 'react';

import { useReelNameGeneration } from './useReelNameGeneration';

import type { ReelNameGenerationContext } from './useReelNameGeneration';
import type { ClipVideoCreatorAdapter } from '@src/components/content-components/hooks/ClipVideoCreatorAdapter';
import type { Timeline } from '@src/editor/models/Timeline';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

const logger = LogManager.getLogger('ReelEditor:useReelSectionNaming');

/** The two things a naming run needs to know about the Section it is naming. */
export type NameableSection = {
  /** Which words the passage is, for the prompt's input. */
  elementIds: string[];
  /** An off Section has no elements on the timeline, so nothing to read. */
  enabled: boolean;
};

export type ReelSectionNaming = {
  /** Stores a name the creator typed; `''` clears back to the fallback. */
  renameSection: (sectionId: string, name: string) => void;
  /** Names the Section from what is said in it, or asks first (DE10). */
  generateSectionName: (
    sectionId: string,
    options?: { userNameConfirmed?: boolean; guidance?: string },
  ) => Promise<void>;
  /** Sections with a generation in flight. */
  generatingIds: ReadonlySet<string>;
  /** The last failure per Section, until its next run. */
  errors: Readonly<Record<string, string | undefined>>;
};

/**
 * PLAT-11054 / PLAT-11059 — naming one Section of a reel: the typed name and
 * the generated one, and the rule that decides when the second may replace the
 * first.
 *
 * Extracted from `ReelEditorPanel` when the Transcript tab's Section headings
 * grew the same two actions. What is shared is not the wiring but the product
 * rule: a generated name may land on a name the creator typed only after they
 * have been asked (DE10), and only if that name is still the one they approved
 * over — a round trip is seconds long, and the creator may type into the row
 * while it is in flight. Two surfaces re-deriving that from the ticket is how
 * one of them ends up quietly losing a name.
 *
 * The dialog is the caller's, not this hook's: `onConfirmOverUserName` reports
 * that an ask is owed and the surface opens whichever dialog it hosts, then
 * re-enters here with `userNameConfirmed`. That keeps the hook renderless and
 * lets the panel go on sharing one dialog between Sections and Chapters.
 *
 * Timeline and Section are read through getters rather than captured: the
 * editor reassigns `timeline` on a source reload, and both callers hold lists
 * that change under an edit.
 */
export function useReelSectionNaming(args: {
  adapter?: ClipVideoCreatorAdapter | null;
  getTimeline: () => Timeline | undefined;
  getSection: (sectionId: string) => NameableSection | undefined;
  /** BY REFERENCE — the backend resolves the Brand Kit from it (DE9). */
  context: ReelNameGenerationContext;
  datoClientStore?: DatoClientStore;
  /** A generation is owed an ask before it replaces this typed name. */
  onConfirmOverUserName: (target: { id: string; currentName?: string }) => void;
  /** Fired on a stored rename, with the provenance read before the write. */
  onRenamed?: (target: {
    sectionId: string;
    nameSourceBefore: 'user' | 'generated' | 'clip';
  }) => void;
}): ReelSectionNaming {
  const {
    adapter,
    getTimeline,
    getSection,
    context,
    datoClientStore,
    onConfirmOverUserName,
    onRenamed,
  } = args;

  const {
    generate: generateName,
    generatingIds,
    errors,
  } = useReelNameGeneration(
    SECTION_NAME_PROMPT_TITLE,
    context,
    datoClientStore,
  );

  // Not an editor command: names live in `clipJson.reel`, not the timeline
  // (DE13), so there is no timeline undo entry and nothing for the renderer to
  // repaint — the adapter writes the variant and schedules the save (DS3).
  const renameSection = useCallback(
    (sectionId: string, name: string) => {
      // PLAT-11063 — read before the write, since the write is what changes it.
      // Renaming away from `'generated'` is the clearest signal a generated
      // name did not land; `'clip'` is the seeded fallback.
      onRenamed?.({
        sectionId,
        nameSourceBefore:
          adapter?.currentVideo?.clipJson?.reel?.sections?.[sectionId]
            ?.nameSource ?? 'clip',
      });
      adapter?.renameReelSection(sectionId, name);
    },
    [adapter, onRenamed],
  );

  const generateSectionName = useCallback(
    async (
      sectionId: string,
      options?: { userNameConfirmed?: boolean; guidance?: string },
    ) => {
      const timeline = getTimeline();
      const section = getSection(sectionId);
      // An off Section has no elements on the timeline (PLAT-11072 DS1), so
      // there is nothing to read — the menu item is already greyed out for it.
      if (!timeline || !section || !section.enabled) return;

      const stored =
        adapter?.currentVideo?.clipJson?.reel?.sections?.[sectionId];
      // DS2 — row-level generation is an explicit act, so DE10 permits it over
      // a hand-typed name; what it does not permit is doing so silently.
      if (stored?.nameSource === 'user' && !options?.userNameConfirmed) {
        onConfirmOverUserName({ id: sectionId, currentName: stored.name });
        return;
      }

      // The name this generation was permitted over — nothing, or the typed one
      // whose replacement was just confirmed. What the in-flight check below
      // compares against, so an approved name is not mistaken for one typed
      // since.
      const permittedOver = stored?.name;

      const name = await generateName(
        sectionId,
        sectionBaseText(timeline, section),
        options?.guidance,
      );
      // Failure keeps the existing name and leaves the reason on the row (DS4)
      // — the hook has already recorded it.
      if (!name) return;
      // Read the provenance again, not the copy this closure checked before the
      // request: a round trip is seconds long and the creator may have typed a
      // name into this very row while it was in flight. Landing a generation on
      // top of that would be DE10 broken by timing rather than by intent, and
      // the typed name would be gone without a prompt.
      const sectionNow =
        adapter?.currentVideo?.clipJson?.reel?.sections?.[sectionId];
      if (
        sectionNow?.nameSource === 'user' &&
        sectionNow.name !== permittedOver
      ) {
        logger.debug('Keeping a name typed while a generation was in flight', {
          sectionId,
        });
        return;
      }
      adapter?.setGeneratedReelSectionName(sectionId, name);
    },
    [adapter, generateName, getSection, getTimeline, onConfirmOverUserName],
  );

  return { renameSection, generateSectionName, generatingIds, errors };
}
